import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { RecentSearch, RecentSearchInput } from '../models/recent-search';
import { SearchType } from '../models/search';

@Injectable({
  providedIn: 'root'
})
export class RecentSearchService {
  private readonly maxSearches = 3;
  private readonly expiryMilliseconds = 60 * 60 * 1000;
  private readonly searchesSubject = new BehaviorSubject<RecentSearch[]>([]);
  private expiryTimer?: ReturnType<typeof setTimeout>;
  private ownerKey?: string;

  public readonly recentSearches$ = this.searchesSubject.asObservable();

  public setOwner(userId: number, clientId: number): void {
    const nextOwnerKey = `${clientId}:${userId}`;

    if (this.ownerKey !== undefined && this.ownerKey !== nextOwnerKey) {
      this.clear();
    }

    this.ownerKey = nextOwnerKey;
  }

  public record(search: RecentSearchInput): void {
    const now = Date.now();
    const identity = this.getIdentity(search);
    const recentSearches = this.searchesSubject.value
      .filter(item => !this.isExpired(item, now))
      .filter(item => this.getIdentity(item) !== identity);

    const updatedSearches = [
      {...search, searchedAt: now},
      ...recentSearches
    ].slice(0, this.maxSearches);

    this.searchesSubject.next(updatedSearches);
    this.scheduleNextExpiry();
  }

  public getRecent(searchType?: SearchType): RecentSearch[] {
    this.removeExpiredSearches();
    const searches = this.searchesSubject.value;

    if (searchType === undefined) {
      return [...searches];
    }

    return searches.filter(search => search.searchType === searchType);
  }

  public clear(): void {
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = undefined;
    }

    this.searchesSubject.next([]);
  }

  private getIdentity(search: Pick<RecentSearch, 'searchType' | 'searchValue'>): string {
    return `${search.searchType}:${search.searchValue.trim().toLocaleUpperCase()}`;
  }

  private isExpired(search: RecentSearch, now: number): boolean {
    return now - search.searchedAt >= this.expiryMilliseconds;
  }

  private removeExpiredSearches(): void {
    const now = Date.now();
    const activeSearches = this.searchesSubject.value.filter(search => !this.isExpired(search, now));

    if (activeSearches.length !== this.searchesSubject.value.length) {
      this.searchesSubject.next(activeSearches);
    }

    this.scheduleNextExpiry();
  }

  private scheduleNextExpiry(): void {
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = undefined;
    }

    const oldestSearch = this.searchesSubject.value[this.searchesSubject.value.length - 1];
    if (!oldestSearch) {
      return;
    }

    const expiryDelay = Math.max(
      0,
      oldestSearch.searchedAt + this.expiryMilliseconds - Date.now()
    );

    this.expiryTimer = setTimeout(() => this.removeExpiredSearches(), expiryDelay);
  }
}
